import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Player, RoomState } from "../types";
import { PhaseTimer } from "./PhaseTimer";

interface Props {
  room: RoomState;
  me: Player;
  onSubmit: (image: string) => void;
}

const FREE_COLORS = [
  "#0b0b14",
  "#ffffff",
  "#ef4444",
  "#f59e0b",
  "#eab308",
  "#22c55e",
  "#0ea5e9",
  "#a855f7",
];
const CATNIP_COLORS = [
  "#fb7185",
  "#f97316",
  "#84cc16",
  "#14b8a6",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#a16207",
  "#7c2d12",
  "#1f2937",
];
const SIZES = [3, 6, 12, 24];

interface Stroke {
  color: string;
  size: number;
  erase: boolean;
  points: Array<{ x: number; y: number }>;
}

export function DrawingCanvas({ room, me, onSubmit }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const undoneRef = useRef<Stroke[]>([]);
  const drawingRef = useRef<Stroke | null>(null);
  const lastSentRef = useRef<number>(0);
  const [color, setColor] = useState<string>(FREE_COLORS[2]!);
  const [size, setSize] = useState<number>(6);
  const [erase, setErase] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const colors = useMemo(
    () => (me.cosmetics.catnipPack ? [...FREE_COLORS, ...CATNIP_COLORS] : FREE_COLORS),
    [me.cosmetics.catnipPack]
  );

  // Initialize / resize the canvas to a square that fills the wrapper.
  useEffect(() => {
    const canvas = canvasRef.current!;
    const wrap = wrapRef.current!;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const cssSize = Math.max(240, Math.min(rect.width, rect.height));
      canvas.style.width = `${cssSize}px`;
      canvas.style.height = `${cssSize}px`;
      canvas.width = Math.floor(cssSize * dpr);
      canvas.height = Math.floor(cssSize * dpr);
      const ctx = canvas.getContext("2d")!;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintAll();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paintAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    ctx.fillStyle = "#fafafa";
    ctx.fillRect(0, 0, w, h);
    for (const s of strokesRef.current) drawStroke(ctx, s);
    if (drawingRef.current) drawStroke(ctx, drawingRef.current);
  }, []);

  // ---- pointer handling ----

  const pointerPos = (e: PointerEvent | React.PointerEvent): { x: number; y: number } => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (submitted) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    const { x, y } = pointerPos(e);
    drawingRef.current = {
      color,
      size,
      erase,
      points: [{ x, y }],
    };
    undoneRef.current = [];
    paintAll();
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drawingRef.current) return;
    const { x, y } = pointerPos(e);
    drawingRef.current.points.push({ x, y });
    paintAll();
  };
  const onPointerUp = () => {
    if (!drawingRef.current) return;
    strokesRef.current.push(drawingRef.current);
    drawingRef.current = null;
    paintAll();
  };

  const undo = () => {
    const s = strokesRef.current.pop();
    if (s) {
      undoneRef.current.push(s);
      paintAll();
    }
  };
  const redo = () => {
    const s = undoneRef.current.pop();
    if (s) {
      strokesRef.current.push(s);
      paintAll();
    }
  };
  const clear = () => {
    strokesRef.current = [];
    undoneRef.current = [];
    paintAll();
  };

  const exportImage = useCallback((): string => {
    const src = canvasRef.current!;
    // Downscale to keep payload small (server caps at ~250kb).
    const target = 480;
    const off = document.createElement("canvas");
    off.width = target;
    off.height = target;
    const ctx = off.getContext("2d")!;
    ctx.drawImage(src, 0, 0, target, target);
    return off.toDataURL("image/png");
  }, []);

  const submit = useCallback(() => {
    if (submitted) return;
    setSubmitted(true);
    onSubmit(exportImage());
  }, [exportImage, onSubmit, submitted]);

  // Auto-submit a snapshot a few seconds before time runs out as a safety net,
  // so nobody loses their drawing to network jank.
  useEffect(() => {
    if (!room.phaseEndsAt || submitted) return;
    const id = window.setInterval(() => {
      const remaining = (room.phaseEndsAt ?? 0) - Date.now();
      if (remaining < 4_000 && Date.now() - lastSentRef.current > 3_500) {
        lastSentRef.current = Date.now();
        onSubmit(exportImage());
      }
    }, 1_000);
    return () => window.clearInterval(id);
  }, [room.phaseEndsAt, submitted, onSubmit, exportImage]);

  return (
    <div className="draw">
      <div className="draw-head">
        <div>
          <h2>Round {room.round}</h2>
          <p className="muted">Capture this cat. Vibes count more than precision.</p>
        </div>
        <PhaseTimer endsAt={room.phaseEndsAt} label="Time left" />
      </div>

      <div className="draw-stage">
        <aside className="reference-panel">
          {room.reference && <img src={room.reference.url} alt="Reference" />}
          <div className="reference-caption muted">your subject</div>
        </aside>

        <div className="canvas-panel" ref={wrapRef}>
          <canvas
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={onPointerUp}
            style={{ touchAction: "none", cursor: submitted ? "not-allowed" : "crosshair" }}
          />
          {submitted && (
            <div className="submitted-overlay">
              <div>Submitted!</div>
              <div className="muted small">Waiting on the rest of the lobby...</div>
            </div>
          )}
        </div>

        <aside className="tools">
          <div className="tool-group">
            <div className="tool-label">Color</div>
            <div className="palette">
              {colors.map((c) => (
                <button
                  key={c}
                  className={`swatch ${color === c && !erase ? "active" : ""}`}
                  style={{ background: c }}
                  onClick={() => {
                    setColor(c);
                    setErase(false);
                  }}
                />
              ))}
            </div>
          </div>
          <div className="tool-group">
            <div className="tool-label">Size</div>
            <div className="sizes">
              {SIZES.map((s) => (
                <button
                  key={s}
                  className={`size-btn ${size === s ? "active" : ""}`}
                  onClick={() => setSize(s)}
                >
                  <span className="dot" style={{ width: s + 4, height: s + 4 }} />
                </button>
              ))}
            </div>
          </div>
          <div className="tool-group tool-row">
            <button className={`ghost ${erase ? "active" : ""}`} onClick={() => setErase((v) => !v)}>
              {erase ? "Erasing" : "Eraser"}
            </button>
            <button className="ghost" onClick={undo}>
              Undo
            </button>
            <button className="ghost" onClick={redo}>
              Redo
            </button>
            <button className="ghost danger" onClick={clear}>
              Clear
            </button>
          </div>
          <button className="primary big" onClick={submit} disabled={submitted}>
            {submitted ? "Submitted" : "Submit drawing"}
          </button>
        </aside>
      </div>
    </div>
  );
}

function drawStroke(ctx: CanvasRenderingContext2D, s: Stroke) {
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.lineWidth = s.size;
  ctx.strokeStyle = s.erase ? "#fafafa" : s.color;
  ctx.beginPath();
  const pts = s.points;
  if (pts.length === 0) {
    ctx.restore();
    return;
  }
  if (pts.length === 1) {
    const p = pts[0]!;
    ctx.fillStyle = ctx.strokeStyle as string;
    ctx.beginPath();
    ctx.arc(p.x, p.y, s.size / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }
  ctx.moveTo(pts[0]!.x, pts[0]!.y);
  for (let i = 1; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    ctx.quadraticCurveTo(a.x, a.y, mx, my);
  }
  const last = pts[pts.length - 1]!;
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
  ctx.restore();
}
